import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, HeartHandshake, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { requestOpenFeedback } from '../../utils/feedbackOpener';
import { SeoHead } from '../common/SeoHead';

type SupportLink = {
  label: string;
  href: string;
};

export const AboutProject = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const handleFeedbackClick = () => {
    if (loading) return;
    if (!user) {
      navigate('/register');
      return;
    }
    requestOpenFeedback();
  };

  const supportLinks: SupportLink[] = [
    {
      label: t('about.support.links.usdt.label', 'USDT (TRC20)'),
      href: '/usdt-trc20-qr.png',
    },
    // {
    //   label: t('about.support.links.monobank.label', 'Monobank jar'),
    //   href: 'https://send.monobank.ua/jar/2pHhLj5RFf',
    // },
    {
      label: t('about.support.links.privatbank.label', 'PrivatBank envelope'),
      href: 'https://www.privat24.ua/send/j3pct',
    },
    {
      label: t('about.support.links.ko-fi.label', 'Ko-fi'),
      href: 'https://ko-fi.com/isayevilya',
    },
  ].filter((l) => Boolean(l.href?.trim()));

  return (
    <>
      <SeoHead page="about" />
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto w-full max-w-4xl px-4 py-10">
          <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 p-6 sm:p-10">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-sm font-medium text-primary-700">
                  {t('common.appName')}
                </div>
                <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
                  {t('about.title', 'About the project')}
                </h1>
                <p className="mt-4 text-gray-700 leading-relaxed">
                  {t(
                    'about.intro',
                    'This is a personal side project I build in my free time. It is an MVP and still in active development — over time I will ship new features and improve it as I have time.'
                  )}
                </p>
                <p className="mt-4 text-gray-700 leading-relaxed">
                  {t(
                    'about.author',
                    "I'm a software developer with 8+ years of experience. I’m also passionate about cycling, and I created this app to make it easy to track costs and history for my car-and-bike fleet."
                  )}
                </p>
              </div>
              <a
                className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                href="/"
              >
                {t('about.backToApp', 'Back to app')}
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6">
              <section className="rounded-xl bg-gray-50 p-5 ring-1 ring-gray-200">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <HeartHandshake className="h-5 w-5 text-primary-700" />
                  {t('about.support.title', 'Help the project')}
                </div>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                  {t(
                    'about.support.description',
                    'If you find this project useful and want to support its development, you can donate using the links below.'
                  )}
                </p>

                <div className="mt-4 flex flex-wrap gap-3">
                  {supportLinks.map((l) => (
                    <a
                      key={l.label}
                      href={l.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-primary-700 ring-1 ring-gray-200 hover:bg-gray-50"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {l.label}
                    </a>
                  ))}
                </div>

                <div className="mt-4 text-sm text-gray-700 whitespace-pre-line">
                  {t(
                    'about.support.details',
                    'Other details (optional):\n- IBAN: ...\n- Bank: ...\n- Purpose: ...'
                  )}
                </div>
              </section>

              <section className="rounded-xl bg-white p-5 ring-1 ring-gray-200">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <MessageSquare className="h-5 w-5 text-primary-700" />
                  {t('about.feedback.title', 'Feedback')}
                </div>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                  {t('about.feedback.description')}
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleFeedbackClick}
                    className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t('about.feedback.button')}
                  </button>
                  {!loading && !user && (
                    <p className="mt-3 text-xs text-gray-500 leading-relaxed max-w-md">
                      {t('about.feedback.guestHint')}
                    </p>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
